import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { doc, getDoc, updateDoc, serverTimestamp, collection, query, onSnapshot, orderBy } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../../firebase';
import Layout from '../../components/ui/Layout';
import PageHeader from '../../components/ui/PageHeader';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import AddModelModal from './AddModelModal';
import { CATEGORIES_BY_USAGE, getCategoryLabel } from '../../constants/categories';
import { getIconByName } from '../../constants/icons';
import { isSpecsComplete } from '../../constants/specSchemas';
import {
  IconPlus, IconArrowLeft, IconBuildingFactory2, IconTractor,
  IconBackhoe, IconScissors, IconCar, IconBike, IconUpload,
  IconEdit, IconCheck, IconX,
} from '@tabler/icons-react';

const TYPE_ICONS = {
  agricultural: IconTractor,
  plant: IconBackhoe,
  groundcare: IconScissors,
  atv: IconBike,
  vehicle: IconCar,
};

function calculateCompleteness(model) {
  const checks = [
    !!model.heroImageUrl,
    !!model.operatorManualUrl,
    !!model.workshopManualUrl,
    !!model.subCategory,
    isSpecsComplete(model.category, model.subCategory, model.specs),
    !!model.serviceScheduleComplete,
  ];
  const done = checks.filter(Boolean).length;
  return Math.round((done / checks.length) * 100);
}

function getCompletenessColor(pct) {
  if (pct >= 100) return { border: '#86EFAC', bg: '#F0FDF4' };
  if (pct >= 76)  return { border: '#FEF08A', bg: '#FEFCE8' };
  if (pct >= 51)  return { border: '#FDBA74', bg: '#FFF7ED' };
  return { border: '#FCA5A5', bg: '#FEF2F2' };
}

export default function MakeDetail() {
  const { makeId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedType = searchParams.get('type');
  const selectedSubCategory = searchParams.get('sub');

  const [make, setMake] = useState(null);
  const [models, setModels] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModel, setShowAddModel] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const logoInputRef = useRef();

  const loadMake = async () => {
    const snap = await getDoc(doc(db, 'makes', makeId));
    if (snap.exists()) setMake({ id: snap.id, ...snap.data() });
    setLoading(false);
  };

  useEffect(() => { loadMake(); }, [makeId]);

  useEffect(() => {
    const q = query(collection(db, 'makes', makeId, 'models'), orderBy('name'));
    const unsub = onSnapshot(q, snap => setModels(snap.docs.map(d => ({ id: d.id, ...d.data() }))));
    return unsub;
  }, [makeId]);

  useEffect(() => {
    if (!selectedType) { setSubCategories([]); return; }
    const q = query(collection(db, 'categories', selectedType, 'subCategories'), orderBy('order'));
    const unsub = onSnapshot(q, snap => setSubCategories(snap.docs.map(d => ({ id: d.id, ...d.data() }))));
    return unsub;
  }, [selectedType]);

  const updateMake = async (changes) => {
    await updateDoc(doc(db, 'makes', makeId), { ...changes, updatedAt: serverTimestamp() });
    await loadMake();
  };

  const handleLogoUpload = async (file) => {
    if (!file) return;
    setUploadingLogo(true);
    try {
      const path = 'makes/' + makeId + '/logo_' + Date.now() + '_' + file.name;
      const storageRef = ref(storage, path);
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);
      await updateMake({ logoUrl: url });
    } catch (err) {
      console.error(err);
      alert('Logo upload failed: ' + err.message);
    }
    setUploadingLogo(false);
  };

  if (loading) return <Layout><div style={{ padding: 40, color: 'var(--color-text-tertiary)' }}>Loading...</div></Layout>;
  if (!make) return (
    <Layout>
      <PageHeader title="Make not found" />
      <div style={{ padding: 28 }}>
        <Button onClick={() => navigate('/library')} icon={<IconArrowLeft size={14} stroke={1.5} />}>Back to library</Button>
      </div>
    </Layout>
  );

  const supportedTypes = make.supportedTypes || [];
  const orderedTypes = CATEGORIES_BY_USAGE.filter(c => supportedTypes.includes(c.code)).map(c => c.code);
  const effectiveType = selectedType;

  const modelsForView = effectiveType
    ? models.filter(m => m.category === effectiveType && (!selectedSubCategory || m.subCategory === selectedSubCategory))
    : [];

  const breadcrumb = (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: 'var(--color-text-tertiary)', marginBottom: 6 }}>
      <span onClick={() => navigate('/library')} style={{ cursor: 'pointer' }}>Library</span>
      <span>/</span>
      <span onClick={() => navigate('/library/makes/' + makeId)} style={{ cursor: 'pointer', color: effectiveType ? 'var(--color-text-tertiary)' : 'var(--color-text-primary)' }}>{make.name}</span>
      {effectiveType && (<><span>/</span><span onClick={() => setSearchParams({ type: effectiveType })} style={{ cursor: 'pointer', color: selectedSubCategory ? 'var(--color-text-tertiary)' : 'var(--color-text-primary)' }}>{getCategoryLabel(effectiveType)}</span></>)}
      {selectedSubCategory && (<><span>/</span><span style={{ color: 'var(--color-text-primary)' }}>{subCategories.find(s => s.code === selectedSubCategory)?.name || selectedSubCategory}</span></>)}
    </div>
  );

  if (!effectiveType) {
    return (
      <Layout>
        <PageHeader
          title={<div>{breadcrumb}<div>{make.name}</div></div>}
          actions={<Button variant="secondary" onClick={() => navigate('/library')} icon={<IconArrowLeft size={14} stroke={1.5} />}>Back</Button>}
        />
        <div style={{ padding: 28 }}>
          <MakeProfileEditor make={make} onUpdate={updateMake} onLogoUpload={() => logoInputRef.current?.click()} uploadingLogo={uploadingLogo} />
          <input ref={logoInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={e => handleLogoUpload(e.target.files?.[0])} />

          <div style={{ fontSize: 10, fontWeight: 500, color: 'var(--color-text-tertiary)', letterSpacing: '0.04em', textTransform: 'uppercase', marginBottom: 8, marginTop: 28 }}>
            Machine types
          </div>
          {orderedTypes.length === 0 ? (
            <Card><div style={{ padding: 20, fontSize: 13, color: 'var(--color-text-tertiary)' }}>No supported types selected. Edit above to add some.</div></Card>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 16 }}>
              {orderedTypes.map(code => {
                const Icon = TYPE_ICONS[code] || IconBuildingFactory2;
                const count = models.filter(m => m.category === code).length;
                return (
                  <div key={code} onClick={() => setSearchParams({ type: code })}
                    style={{ aspectRatio: '1', background: 'var(--color-bg-primary)', border: '0.5px solid var(--color-border-light)', borderRadius: 'var(--radius-lg)', padding: 16, cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12, transition: 'all 0.15s' }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--color-border-medium)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--color-border-light)'; e.currentTarget.style.transform = 'translateY(0)'; }}>
                    <Icon size={42} stroke={1.2} color="var(--color-brand)" />
                    <div style={{ fontSize: 14, fontWeight: 500 }}>{getCategoryLabel(code)}</div>
                    <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)' }}>{count} model{count !== 1 ? 's' : ''}</div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </Layout>
    );
  }

  if (effectiveType && !selectedSubCategory && subCategories.length > 0) {
    return (
      <Layout>
        <PageHeader
          title={<div>{breadcrumb}<div>{make.name} — {getCategoryLabel(effectiveType)}</div></div>}
          subtitle="Pick a sub-category to see models"
          actions={<Button variant="secondary" onClick={() => setSearchParams({})} icon={<IconArrowLeft size={14} stroke={1.5} />}>Back</Button>}
        />
        <div style={{ padding: 28 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 16 }}>
            {subCategories.map(sc => {
              const Icon = getIconByName(sc.iconName);
              const count = models.filter(m => m.category === effectiveType && m.subCategory === sc.code).length;
              return (
                <div key={sc.id} onClick={() => setSearchParams({ type: effectiveType, sub: sc.code })}
                  style={{ aspectRatio: '1', background: 'var(--color-bg-primary)', border: '0.5px solid var(--color-border-light)', borderRadius: 'var(--radius-lg)', padding: 16, cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12, transition: 'all 0.15s' }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--color-border-medium)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--color-border-light)'; e.currentTarget.style.transform = 'translateY(0)'; }}>
                  <Icon size={42} stroke={1.2} color="var(--color-brand)" />
                  <div style={{ fontSize: 14, fontWeight: 500, textAlign: 'center' }}>{sc.name}</div>
                  <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)' }}>{count} model{count !== 1 ? 's' : ''}</div>
                </div>
              );
            })}
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <PageHeader
        title={<div>{breadcrumb}<div>{make.name} — {selectedSubCategory ? (subCategories.find(s => s.code === selectedSubCategory)?.name || selectedSubCategory) : getCategoryLabel(effectiveType)}</div></div>}
        subtitle={modelsForView.length + ' model' + (modelsForView.length !== 1 ? 's' : '')}
        actions={
          <>
            <Button variant="secondary" onClick={() => {
              if (selectedSubCategory) setSearchParams({ type: effectiveType });
              else setSearchParams({});
            }} icon={<IconArrowLeft size={14} stroke={1.5} />}>Back</Button>
            <Button icon={<IconPlus size={14} stroke={2} />} onClick={() => setShowAddModel(true)} disabled={!effectiveType}>New model</Button>
          </>
        }
      />
      <div style={{ padding: 28 }}>
        {modelsForView.length === 0 ? (
          <Card>
            <div style={{ padding: '40px 20px', textAlign: 'center' }}>
              <div style={{ fontSize: 14, color: 'var(--color-text-secondary)', marginBottom: 6 }}>No models here yet</div>
              <div style={{ fontSize: 12, color: 'var(--color-text-tertiary)' }}>Click "New model" to add the first one.</div>
            </div>
          </Card>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 16 }}>
            {modelsForView.map(model => {
              const pct = calculateCompleteness(model);
              const colors = getCompletenessColor(pct);
              return (
                <div key={model.id} onClick={() => navigate('/library/makes/' + makeId + '/models/' + model.id)}
                  style={{ aspectRatio: '1', background: 'var(--color-bg-primary)', border: '1px solid ' + colors.border, borderRadius: 'var(--radius-lg)', padding: 16, cursor: 'pointer', display: 'flex', flexDirection: 'column', transition: 'all 0.15s', position: 'relative' }}
                  onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = 'var(--shadow-sm)'; }}
                  onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}>
                  <div style={{ position: 'absolute', top: 8, right: 8, fontSize: 10, fontWeight: 500, padding: '2px 7px', borderRadius: 'var(--radius-full)', background: colors.bg, color: 'var(--color-text-secondary)' }}>
                    {pct}%
                  </div>
                  <div style={{ flex: 1, background: 'var(--color-bg-secondary)', borderRadius: 'var(--radius-md)', marginBottom: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                    {model.heroImageUrl ? <img src={model.heroImageUrl} alt={model.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <IconTractor size={36} stroke={1} color="var(--color-text-tertiary)" />}
                  </div>
                  <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 2 }}>{model.name}</div>
                  <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)' }}>{model.yearRange || '—'}</div>
                  {!model.subCategory && <div style={{ marginTop: 4, fontSize: 9, color: 'var(--color-warning)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Needs sub-category</div>}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {showAddModel && effectiveType && (
        <AddModelModal
          makeId={makeId}
          makeName={make.name}
          category={effectiveType}
          categoryLabel={getCategoryLabel(effectiveType)}
          defaultSubCategory={selectedSubCategory}
          subCategories={subCategories}
          onClose={() => setShowAddModel(false)}
        />
      )}
    </Layout>
  );
}

function MakeProfileEditor({ make, onUpdate, onLogoUpload, uploadingLogo }) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(make.name);
  const [country, setCountry] = useState(make.country || '');
  const [description, setDescription] = useState(make.description || '');
  const [supportedTypes, setSupportedTypes] = useState(make.supportedTypes || []);
  const [saving, setSaving] = useState(false);

  const toggleType = (code) => {
    setSupportedTypes(prev => prev.includes(code) ? prev.filter(t => t !== code) : [...prev, code]);
  };

  const save = async () => {
    if (!name.trim()) return;
    if (supportedTypes.length === 0) return;
    setSaving(true);
    await onUpdate({
      name: name.trim(),
      country: country.trim() || null,
      description: description.trim() || null,
      supportedTypes,
    });
    setSaving(false);
    setEditing(false);
  };

  const cancel = () => {
    setName(make.name);
    setCountry(make.country || '');
    setDescription(make.description || '');
    setSupportedTypes(make.supportedTypes || []);
    setEditing(false);
  };

  return (
    <Card padding={0}>
      <div style={{ padding: 24, display: 'flex', gap: 24, alignItems: 'flex-start' }}>
        <div style={{ flexShrink: 0 }}>
          <div style={{ width: 120, height: 120, borderRadius: 12, background: 'var(--color-bg-secondary)', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', border: '0.5px solid var(--color-border-light)' }}>
            {make.logoUrl ? <img src={make.logoUrl} alt={make.name} style={{ maxWidth: '80%', maxHeight: '80%', objectFit: 'contain' }} /> : <IconBuildingFactory2 size={48} stroke={1} color="var(--color-text-tertiary)" />}
          </div>
          <Button fullWidth variant="secondary" size="sm" loading={uploadingLogo} icon={<IconUpload size={12} stroke={1.5} />} onClick={onLogoUpload} style={{ marginTop: 8 }}>
            {make.logoUrl ? 'Replace' : 'Upload logo'}
          </Button>
        </div>

        <div style={{ flex: 1 }}>
          {editing ? (
            <>
              <div style={{ marginBottom: 12 }}>
                <label style={{ fontSize: 10, color: 'var(--color-text-tertiary)', display: 'block', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Make name</label>
                <input value={name} onChange={e => setName(e.target.value)} />
              </div>
              <div style={{ marginBottom: 12 }}>
                <label style={{ fontSize: 10, color: 'var(--color-text-tertiary)', display: 'block', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Country of origin</label>
                <input value={country} onChange={e => setCountry(e.target.value)} />
              </div>
              <div style={{ marginBottom: 12 }}>
                <label style={{ fontSize: 10, color: 'var(--color-text-tertiary)', display: 'block', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Description</label>
                <textarea value={description} onChange={e => setDescription(e.target.value)} rows={3} style={{ resize: 'vertical', minHeight: 60 }} />
              </div>
              <div style={{ marginBottom: 12 }}>
                <label style={{ fontSize: 10, color: 'var(--color-text-tertiary)', display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Supported machine types</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {CATEGORIES_BY_USAGE.map(cat => {
                    const active = supportedTypes.includes(cat.code);
                    return (
                      <div key={cat.code} onClick={() => toggleType(cat.code)}
                        style={{ padding: '5px 12px', borderRadius: 'var(--radius-full)', background: active ? 'var(--color-brand)' : 'var(--color-bg-primary)', color: active ? '#fff' : 'var(--color-text-secondary)', border: active ? 'none' : '0.5px solid var(--color-border-medium)', fontSize: 12, cursor: 'pointer', transition: 'all 0.15s' }}>
                        {cat.label}
                      </div>
                    );
                  })}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <Button variant="secondary" size="sm" onClick={cancel} icon={<IconX size={12} stroke={2} />}>Cancel</Button>
                <Button size="sm" loading={saving} onClick={save} icon={<IconCheck size={12} stroke={2} />}>Save changes</Button>
              </div>
            </>
          ) : (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                <div style={{ fontSize: 22, fontWeight: 500 }}>{make.name}</div>
                <div onClick={() => setEditing(true)} style={{ cursor: 'pointer', padding: 6, color: 'var(--color-text-tertiary)' }}>
                  <IconEdit size={16} stroke={1.5} />
                </div>
              </div>
              {make.country && <div style={{ fontSize: 13, color: 'var(--color-text-secondary)', marginBottom: 12 }}>{make.country}</div>}
              {make.description && <div style={{ fontSize: 13, color: 'var(--color-text-secondary)', lineHeight: 1.5, marginBottom: 16 }}>{make.description}</div>}
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {(make.supportedTypes || []).map(code => (
                  <span key={code} style={{ fontSize: 10, padding: '3px 8px', borderRadius: 'var(--radius-full)', background: 'var(--color-bg-tertiary)', color: 'var(--color-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                    {getCategoryLabel(code)}
                  </span>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </Card>
  );
}
